import React from 'react';

import GithubDrawer from '../components/drawers/GithubDrawer';
import LinearDrawer from '../components/drawers/LinearDrawer';
import GitlabDrawer from '../components/drawers/GitlabDrawer';
import BitbucketDrawer from '../components/drawers/BitbucketDrawer';
import GithubDependabotDrawer from '../components/drawers/GithubDependabotDrawer';
import ClickUpDrawer from '../components/drawers/ClickUpDrawer';
import JiraDrawer from '../components/drawers/JiraDrawer';
import ShortcutDrawer from '../components/drawers/ShortcutDrawer';
import NotionDrawer from '../components/drawers/NotionDrawer';
import SlackDrawer from '../components/drawers/SlackDrawer';
import {exchangeCodeForGitHubToken} from 'app/Integrations/Github';
import {exchangeCodeForGitlabToken} from 'app/Integrations/Gitlab';
import {exchangeCodeForBitbucketToken} from 'app/Integrations/Bitbucket';
import {exchangeCodeForLinearToken} from 'app/Integrations/Linear';
import {exchangeCodeForClickUpToken} from 'app/Integrations/ClickUp';
import {exchangeCodeForJiraToken} from 'app/Integrations/Jira';
import {exchangeCodeForNotionToken} from 'app/Integrations/Notion';
import AWSDrawer from '../components/drawers/AWSDrawer';
import { exchangeCodeForSlackToken } from 'app/Integrations/Slack';
import AikidoSecurityDrawer from '../components/drawers/AikidoSecurityDrawer';
import { exchangeCodeForAikidoToken } from 'app/Integrations/Aikido';
import GCPDrawer from '../components/drawers/GCPDrawer';
import GoogleWorkspaceDrawer from '../components/drawers/GoogleWorkspaceDrawer';
import { exchangeCodeForGoogleWorkspaceToken } from 'app/Integrations/GoogleWorkspace';
import CloudflareDrawer from '../components/drawers/CloudflareDrawer';

export interface Integration {
  id: string;
  name: string;
  group: string;
  img_path: string;
  status: string;
}

export interface TokenExchangeConfig {
  [key: string]: (code: string, state: string, currentUser: any, navigate: any) => void;
}

export interface DrawerComponentConfig {
  [key: string]: React.ComponentType<{
    id?: string;
    isOpen: boolean;
    onClose: () => void;
  }>;
}

export interface IntegrationCategory {
  value: string;
  label: string;
}

export const tokenExchangeMap: TokenExchangeConfig = {
  GitHub: (code, _state, currentUser, navigate) => 
    exchangeCodeForGitHubToken(code, currentUser, navigate),
  GitLab: (code, _state, currentUser, navigate) => 
    exchangeCodeForGitlabToken(code, currentUser, navigate),
  Bitbucket: (code, _state, currentUser, navigate) => 
    exchangeCodeForBitbucketToken(code, currentUser, navigate),
  Linear: (code, state, currentUser, navigate) => 
    exchangeCodeForLinearToken(code, state, currentUser, navigate),
  ClickUp: (code, _state, currentUser, navigate) => 
    exchangeCodeForClickUpToken(code, currentUser, navigate),
  Jira: (code, _state, currentUser, navigate) => 
    exchangeCodeForJiraToken(code, currentUser, navigate),
  Notion: (code, _state, currentUser, navigate) => 
    exchangeCodeForNotionToken(code, currentUser, navigate),
  Slack: (code, _state, currentUser, navigate) =>
    exchangeCodeForSlackToken(code, currentUser, navigate),
  'Aikido Security': (code, _state, currentUser, navigate) =>
    exchangeCodeForAikidoToken(code, currentUser, navigate),
  'Google Workspace': (code, _state, currentUser, navigate) =>
    exchangeCodeForGoogleWorkspaceToken(code, currentUser, navigate)
};

export const drawerComponentMap: DrawerComponentConfig = {
  GitHub: GithubDrawer,
  GitLab: GitlabDrawer,
  Linear: LinearDrawer,
  Bitbucket: BitbucketDrawer,
  'Github Dependabot': GithubDependabotDrawer,
  ClickUp: ClickUpDrawer,
  Jira: JiraDrawer,
  Shortcut: ShortcutDrawer,
  Notion: NotionDrawer,
  AWS: AWSDrawer,
  Slack: SlackDrawer,
  'Aikido Security': AikidoSecurityDrawer,
  'Google Cloud Platform': GCPDrawer,
  'Google Workspace': GoogleWorkspaceDrawer,
  Cloudflare: CloudflareDrawer
};

export const integrationCategories: IntegrationCategory[] = [
  { value: '', label: 'All' },
  { value: 'Cloud Providers', label: 'Cloud Services' },
  { value: 'Business Suite', label: 'Business Suite' },
  { value: 'Background Checks', label: 'Background Checks' },
  { value: 'Device Management', label: 'Device Management' },
  { value: 'Developer Tools', label: 'Developer Tools' },
  { value: 'Endpoint Security', label: 'Endpoint Security' },
  { value: 'Human Resources', label: 'Human Resources' },
  { value: 'Password Management', label: 'Password Management' },
  { value: 'Single Sign-On', label: 'Single Sign On' },
  { value: 'Project Management', label: 'Project Management' },
  { value: 'Vulnerability Management', label: 'Vulnerability Management' },
  { value: 'Other', label: 'Other' }
];

export const handleOAuthCallback = (
  code: string | null, 
  state: string | null, 
  integrations: Integration[], 
  currentUser: any,
  navigate: any
): void => {
  if (!code) return;
  
  const activePendingIntegration = localStorage.getItem('pendingIntegration');
  
  if (activePendingIntegration && tokenExchangeMap[activePendingIntegration]) {
    const hasIntegration = integrations.some(
      item => item.name === activePendingIntegration && item.status === 'Connected'
    );
    
    if (!hasIntegration) {
      tokenExchangeMap[activePendingIntegration](code, state || '', currentUser, navigate);
    }
  }
  
  localStorage.removeItem('pendingIntegration');
};

export const renderActiveDrawer = (
  activeIntegration: string, 
  isModalOpen: boolean, 
  selectedItemId: string,
  onClose: () => void
): React.ReactNode => {
  if (!activeIntegration || !isModalOpen) return null;
  
  const DrawerComponent = drawerComponentMap[activeIntegration];
  
  if (!DrawerComponent) return null;
  
  return React.createElement(DrawerComponent, {
    id: selectedItemId,
    isOpen: isModalOpen,
    onClose: onClose,
  });
};

export const filterIntegrations = (
  integrations: Integration[],
  searchTerm: string,
  category: string
): Integration[] => {
  let filtered = [...integrations];

  if (category && category !== 'all') {
    filtered = filtered.filter(item => item.group === category);
  }

  if (searchTerm && searchTerm.trim() !== '') {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(item => 
      (item.name || '').toLowerCase().includes(term)
    );
  }

  return filtered;
};